import { X, Download, FileText, FileSpreadsheet, Presentation, File as FileIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface FileViewerModalProps {
  file: {
    filename: string;
    file_path: string;
    file_type?: string;
    file_size?: number;
  };
  onClose: () => void;
  onDelete?: () => void;
  canEdit?: boolean;
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getFileTypeInfo(filename: string, mimeType?: string) {
  const ext = filename.split('.').pop()?.toLowerCase() || '';
  const type = mimeType?.toLowerCase() || '';

  if (type.startsWith('image/') || ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { category: 'image', icon: FileIcon, label: 'Image', color: 'text-blue-400' };
  }

  if (type === 'application/pdf' || ext === 'pdf') {
    return { category: 'pdf', icon: FileText, label: 'PDF Document', color: 'text-red-400' };
  }

  if (type.startsWith('video/') || ['mp4', 'webm', 'ogg', 'mov', 'avi'].includes(ext)) {
    return { category: 'video', icon: FileIcon, label: 'Video', color: 'text-purple-400' };
  }

  if (type.startsWith('audio/') || ['mp3', 'wav', 'ogg', 'm4a', 'aac'].includes(ext)) {
    return { category: 'audio', icon: FileIcon, label: 'Audio', color: 'text-green-400' };
  }

  if (['doc', 'docx'].includes(ext) || type.includes('wordprocessingml') || type.includes('msword')) {
    return { category: 'word', icon: FileText, label: 'Word Document', color: 'text-blue-500' };
  }

  if (['xls', 'xlsx'].includes(ext) || type.includes('spreadsheetml') || type.includes('ms-excel')) {
    return { category: 'excel', icon: FileSpreadsheet, label: 'Excel Spreadsheet', color: 'text-green-500' };
  }

  if (['ppt', 'pptx'].includes(ext) || type.includes('presentationml') || type.includes('ms-powerpoint')) {
    return { category: 'powerpoint', icon: Presentation, label: 'PowerPoint Presentation', color: 'text-orange-500' };
  }

  return { category: 'other', icon: FileIcon, label: 'File', color: 'text-zinc-400' };
}

export default function FileViewerModal({ file, onClose, onDelete, canEdit }: FileViewerModalProps) {
  const fileInfo = getFileTypeInfo(file.filename, file.file_type);
  const Icon = fileInfo.icon;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = file.file_path;
    link.download = file.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const renderContent = () => {
    switch (fileInfo.category) {
      case 'image':
        return (
          <div className="flex items-center justify-center p-4 bg-black/50 rounded-lg">
            <img
              src={file.file_path}
              alt={file.filename}
              className="max-w-full max-h-[60vh] w-auto h-auto object-contain rounded"
            />
          </div>
        );

      case 'pdf':
        return (
          <div className="relative w-full h-[70vh] bg-zinc-800 rounded-lg overflow-hidden">
            <iframe
              src={file.file_path}
              className="w-full h-full"
              title={file.filename}
            />
          </div>
        );

      case 'video':
        return (
          <div className="flex items-center justify-center p-4 bg-black/50 rounded-lg">
            <video
              src={file.file_path}
              controls
              className="max-w-full max-h-[60vh] w-auto rounded"
            >
              Your browser does not support video playback.
            </video>
          </div>
        );

      case 'audio':
        return (
          <div className="flex flex-col items-center justify-center p-12 bg-zinc-800/50 rounded-lg">
            <Icon className={`w-24 h-24 mb-6 ${fileInfo.color}`} />
            <audio
              src={file.file_path}
              controls
              className="w-full max-w-md"
            >
              Your browser does not support audio playback.
            </audio>
          </div>
        );

      case 'word':
      case 'excel':
      case 'powerpoint':
      case 'other':
        return (
          <div className="flex flex-col items-center justify-center p-12 bg-zinc-800/50 rounded-lg text-center">
            <div className={`w-24 h-24 rounded-2xl bg-zinc-800 flex items-center justify-center mb-6 border border-zinc-700`}>
              <Icon className={`w-12 h-12 ${fileInfo.color}`} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">{fileInfo.label}</h3>
            <p className="text-zinc-400 mb-1">{file.filename}</p>
            <p className="text-zinc-500 text-sm mb-8">{formatFileSize(file.file_size)}</p>

            {fileInfo.category !== 'other' && (
              <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 mb-6 max-w-md">
                <p className="text-sm text-zinc-300 mb-2">
                  This file will open in {fileInfo.label === 'Word Document' ? 'Microsoft Word' :
                                         fileInfo.label === 'Excel Spreadsheet' ? 'Microsoft Excel' :
                                         fileInfo.label === 'PowerPoint Presentation' ? 'Microsoft PowerPoint' :
                                         'the appropriate application'}
                </p>
                <p className="text-xs text-zinc-500">
                  Click the button below to download and open the file
                </p>
              </div>
            )}

            <Button
              onClick={handleDownload}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg h-auto"
            >
              <Download className="w-5 h-5 mr-2" />
              Download & Open
            </Button>
          </div>
        );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl max-h-[90vh] bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-zinc-800">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Icon className={`w-5 h-5 flex-shrink-0 ${fileInfo.color}`} />
            <div className="flex-1 min-w-0">
              <h2 className="text-white font-semibold truncate">{file.filename}</h2>
              <p className="text-sm text-zinc-500">{fileInfo.label} • {formatFileSize(file.file_size)}</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {fileInfo.category !== 'image' && (
              <Button
                variant="ghost"
                size="icon"
                onClick={handleDownload}
                className="text-zinc-400 hover:text-white hover:bg-zinc-800"
                title="Download file"
              >
                <Download className="w-5 h-5" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
